import React, { useEffect, useMemo, useState } from "react";
import axios from "../../axios";
import {
  GraduationCap,
  Eye,
  Trophy,
  FileText,
  Layers3,
  ClipboardCheck,
  Clock3,
  CheckCircle2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

function CompletedCompetencyPage({ filter }) {
  const [completedCompetencyList, setCompletedAssessmentList] = useState([]);
  const [isGettingData, setIsGettingData] = useState(true);

  const navigate = useNavigate();

  const schoolId = JSON.parse(localStorage.getItem("schoolId"));

  const teacherId = JSON.parse(localStorage.getItem("Teacher_id"));

  let timeTableSessionId = JSON.parse(
    localStorage.getItem("timeTableSessionId"),
  );
  const academicSessionId = JSON.parse(
    localStorage.getItem("academicYearSessionId"),
  );

  const getCompletedAssessment = async () => {
    setIsGettingData(true);

    try {
      const res = await axios.get(
        `/api/v1/competency/evaluations?schoolId=${schoolId}&status=completed&academicYearSessionId=${academicSessionId}&timetableSessionId=${timeTableSessionId}&teacherId=${teacherId}` +
          `${filter?.standard_id ? `&standardId=${filter.standard_id}` : ""}` +
          `${filter?.section_id ? `&sectionId=${filter.section_id}` : ""}`,
      );

      console.log("Completed Competency Assessment API Response:", res?.data);
      console.log("Completed Competency Assessments:", res?.data?.data);

      setCompletedAssessmentList(res?.data?.data || []);
    } catch (error) {
      console.log("Completed Competency Assessment API Error:", error);
      setCompletedAssessmentList([]);
    } finally {
      setIsGettingData(false);
    }
  };

  useEffect(() => {
    getCompletedAssessment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // ─── 🔥 FRONTEND SEARCH FILTER ──────────────────
  const filteredList = useMemo(() => {
    if (!filter?.searchvalue) {
      return completedCompetencyList;
    }

    const searchTerm = filter.searchvalue.toLowerCase().trim();
    return completedCompetencyList.filter((assessment) => {
      const nameMatch =
        assessment?.name?.toLowerCase().includes(searchTerm) || false;
      const competencyMatch =
        assessment?.competencyId?.name?.toLowerCase().includes(searchTerm) ||
        false;
      const domainMatch =
        assessment?.domainId?.name?.toLowerCase().includes(searchTerm) || false;
      const classMatch =
        assessment?.standardId?.name?.toLowerCase().includes(searchTerm) ||
        false;
      const sectionMatch =
        assessment?.sectionId?.name?.toLowerCase().includes(searchTerm) ||
        false;
      const evalTypeMatch =
        assessment?.evaluationTypeId?.name
          ?.toLowerCase()
          .includes(searchTerm) || false;
      // 👈 Assessment Event (backward compatible with reportCycleId)
      const assessmentEventMatch =
        assessment?.assessmentEventId?.name
          ?.toLowerCase()
          .includes(searchTerm) ||
        assessment?.reportCycleId?.name?.toLowerCase().includes(searchTerm) ||
        false;

      return (
        nameMatch ||
        competencyMatch ||
        domainMatch ||
        classMatch ||
        sectionMatch ||
        evalTypeMatch ||
        assessmentEventMatch
      );
    });
  }, [completedCompetencyList, filter?.searchvalue]);

  const handleView = (assessment) => {
    navigate("/completed-competency-report", {
      state: {
        assessmentId: assessment?._id,
        assessment,
      },
    });
  };

  const getRubricScale = (assessment) => {
    const rubricScale = assessment?.rubricScaleId;

    if (!rubricScale) {
      return "N/A";
    }

    const rubricItems = Array.isArray(rubricScale?.rubricItems)
      ? rubricScale.rubricItems
      : [];

    return rubricItems.length > 0
      ? `${rubricScale.name} (${rubricItems.length} Levels)`
      : rubricScale.name || "N/A";
  };

  const getStatusBadge = (status) => {
    if (status?.toLowerCase() === "completed") {
      return {
        text: "Completed",
        icon: <CheckCircle2 size={12} />,
        bg: "#dcfce7",
        color: "#166534",
        border: "#bbf7d0",
      };
    }

    return {
      text: status || "N/A",
      icon: <Clock3 size={12} />,
      bg: "#f3f4f6",
      color: "#4b5563",
      border: "#e5e7eb",
    };
  };

  if (isGettingData) {
    return (
      <div
        className="w-100 d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <div
          className="spinner-border"
          style={{ color: "#f77f00" }}
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (filteredList.length === 0) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center py-5 w-100"
        style={{
          minHeight: "300px",
          color: "#9ca3af",
        }}
      >
        <GraduationCap
          size={48}
          className="mb-3"
          style={{
            opacity: 0.4,
            color: "#d1d5db",
          }}
        />

        <p className="fw-semibold fs-5" style={{ color: "#4b5563" }}>
          🧾 No Completed Competency Found
        </p>

        <p className="small">
          Try adjusting filters or complete an competency.
        </p>
      </div>
    );
  }

  return (
    <div
      className="w-100"
      style={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}
    >
      <div
        className="table-responsive"
        style={{
          maxHeight: "calc(100vh - 200px)",
          overflow: "auto",
        }}
      >
        <table
          className="table align-middle mb-0"
          style={{
            fontSize: "13px",
            minWidth: "1200px",
          }}
        >
          <thead>
            <tr
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: "#f9fafb",
              }}
            >
              <th style={headerStyle}>Class</th>
              <th style={headerStyle}>Section</th>
              <th style={headerStyle}>Assessment</th>
              <th style={headerStyle}>Domain</th>
              <th style={headerStyle}>Competency</th>
              <th style={headerStyle}>Evaluation Type</th>
              {/* 👈 renamed from Report Cycle */}
              <th style={headerStyle}>Assessment Event</th>
              <th style={headerStyle}>Rubric Scale</th>
              <th style={headerStyle}>Status</th>
              <th style={headerStyle}>Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredList.map((assessment, index) => {
              const statusBadge = getStatusBadge(assessment?.status);

              const isLast = index === completedCompetencyList.length - 1;

              return (
                <tr
                  key={assessment?._id}
                  style={{
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    borderBottom: isLast ? "none" : "1px solid #f3f4f6",
                  }}
                  onClick={() => handleView(assessment)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fffbeb";
                    e.currentTarget.style.transform = "scale(1.001)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  {/* CLASS */}
                  <td className="text-center py-3 px-3">
                    <span
                      style={{
                        backgroundColor: "#f3f4f6",
                        color: "#374151",
                        padding: "5px 11px",
                        borderRadius: "8px",
                        fontSize: "12px",
                        fontWeight: 700,
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      {assessment?.standardId?.name || "N/A"}
                    </span>
                  </td>

                  {/* SECTION */}
                  <td className="text-center py-3 px-3">
                    <span
                      style={{
                        backgroundColor: "#fef3c7",
                        color: "#92400e",
                        padding: "5px 11px",
                        borderRadius: "8px",
                        fontSize: "12px",
                        fontWeight: 700,
                        border: "1px solid #fde68a",
                      }}
                    >
                      {assessment?.sectionId?.name || "N/A"}
                    </span>
                  </td>

                  {/* ASSESSMENT */}
                  <td className="py-3 px-3">
                    <div
                      className="d-flex align-items-center gap-2"
                      style={{ minWidth: "240px" }}
                    >
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "9px",
                          backgroundColor: "#f0fdf4",
                          color: "#16a34a",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <ClipboardCheck size={17} />
                      </div>

                      <div>
                        <div
                          style={{
                            fontWeight: 700,
                            color: "#1f2937",
                            lineHeight: "18px",
                          }}
                        >
                          {assessment?.name || "N/A"}
                        </div>

                        {assessment?.description && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#9ca3af",
                              marginTop: "2px",
                              maxWidth: "240px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {assessment.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* DOMAIN */}
                  <td className="text-center py-3 px-3">
                    <span
                      style={{
                        backgroundColor: "#f0fdf4",
                        color: "#15803d",
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "6px 12px",
                        borderRadius: "8px",
                        border: "1px solid #bbf7d0",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Layers3 size={12} />
                      {assessment?.domainId?.name || "N/A"}
                    </span>
                  </td>

                  {/* COMPETENCY */}
                  <td className="text-center py-3 px-3">
                    <span
                      style={{
                        backgroundColor: "#eff6ff",
                        color: "#1d4ed8",
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "6px 12px",
                        borderRadius: "8px",
                        border: "1px solid #bfdbfe",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <GraduationCap size={12} />
                      {assessment?.competencyId?.name || "N/A"}
                    </span>
                  </td>

                  {/* EVALUATION TYPE */}
                  <td className="text-center py-3 px-3">
                    <span
                      style={{
                        backgroundColor: "#fdf4ff",
                        color: "#7c3aed",
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "6px 12px",
                        borderRadius: "8px",
                        border: "1px solid #e9d5ff",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <FileText size={12} />
                      {assessment?.evaluationTypeId?.name || "N/A"}
                    </span>
                  </td>

                  {/* ASSESSMENT EVENT (was Report Cycle) */}
                  <td className="text-center py-3 px-3">
                    <span
                      style={{
                        backgroundColor: "#fff7ed",
                        color: "#c2410c",
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "6px 12px",
                        borderRadius: "8px",
                        border: "1px solid #fed7aa",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Trophy size={12} />
                      {/* 👈 Assessment Event name — backward compatible */}
                      {assessment?.assessmentEventId?.name ||
                        assessment?.reportCycleId?.name ||
                        "N/A"}
                    </span>
                  </td>

                  {/* RUBRIC SCALE */}
                  <td className="text-center py-3 px-3">
                    <span
                      style={{
                        backgroundColor: "#f0f9ff",
                        color: "#0369a1",
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "6px 12px",
                        borderRadius: "8px",
                        border: "1px solid #bae6fd",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getRubricScale(assessment)}
                    </span>
                  </td>

                  {/* STATUS */}
                  <td className="text-center py-3 px-3">
                    <span
                      style={{
                        backgroundColor: statusBadge.bg,
                        color: statusBadge.color,
                        fontSize: "11px",
                        fontWeight: 700,
                        padding: "6px 12px",
                        borderRadius: "20px",
                        border: `1px solid ${statusBadge.border}`,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {statusBadge.icon}
                      {statusBadge.text}
                    </span>
                  </td>

                  {/* ACTION */}
                  <td className="text-center py-3 px-3">
                    <button
                      className="btn d-inline-flex align-items-center gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleView(assessment);
                      }}
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#fff",
                        background:
                          "linear-gradient(135deg, #f77f00 0%, #ff9f43 100%)",
                        border: "none",
                        padding: "8px 18px",
                        borderRadius: "10px",
                        boxShadow: "0 4px 12px rgba(247,127,0,0.25)",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 20px rgba(247,127,0,0.35)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(247,127,0,0.25)";
                      }}
                    >
                      <Eye size={14} />
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const headerStyle = {
  className: "text-center fw-bold py-3 px-3",
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "1px",
  color: "#6b7280",
  backgroundColor: "#f9fafb",
  borderBottom: "2px solid #e5e7eb",
  whiteSpace: "nowrap",
};

export default CompletedCompetencyPage;
